import React, { useState, useEffect, useRef } from 'react';
import { Empty, Skeleton, Carousel } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import productHero from '../../assets/images/banner.png';

// Import local product assets
import { useNavigate } from 'react-router-dom';
import httpClient from '../../utils/HttpClient';
import { PRODUCT_URL, PRODUCT_CATEGORY_URL } from '../../constants/Url';

import { useDispatch, useSelector } from 'react-redux';
import {
  setProductCategoryAction,
  setProductListAction,
  setProductFeaturedAction,
  setProductBannersAction
} from '../../modules/dataWebsite/actions';
import { PRODUCT_BANNERS_GET } from '../../modules/dataWebsite/constants';
import { stripHtml } from '../../utils/Utils';

export default function ProductPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const bannerImages = useSelector((state) => state.dataWebsiteReducer?.productBanners);
  const carouselRef = useRef(null);

  const categories = useSelector(state => state.dataWebsiteReducer?.productCategoryData || [{ _id: 'ALL', name: '全部' }]);
  const productsObj = useSelector(state => state.dataWebsiteReducer?.productData || { list: [], total: 0 });
  const products = productsObj.list || [];
  const totalProducts = productsObj.total || 0;
  const featuredRawProducts = useSelector(state => state.dataWebsiteReducer?.productFeaturedData || []);

  const [pageSize, setPageSize] = useState(8);
  const navigate = useNavigate();

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await httpClient.get(PRODUCT_CATEGORY_URL);
        if (res.data) {
          const fetchedCategories = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : (res.data?.list || []));
          setProductCategoryAction([{ _id: 'ALL', name: '全部' }, ...fetchedCategories]);
        }
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch featured products (constant, category=ALL)
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await httpClient.get(`${PRODUCT_URL}?keyword=&category=ALL&pageNo=1&pageSize=4&isFeature=true`);
        if (res.data) {
          const list = res.data?.data || res.data?.list || res.data?.records || res.data || [];
          setProductFeaturedAction(Array.isArray(list) ? list : []);
        }
      } catch (error) {
        console.error("Error fetching featured products", error);
      }
    };
    fetchFeatured();
  }, []);

  // Fetch product banners
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await httpClient.get('/banners?pageNo=1&pageSize=9&sortKeys[privateFile.uploadedTS]=asc&category=产品');
        if (res.data) {
          const list = res.data?.data || res.data?.list || res.data?.records || res.data || [];
          const images = Array.isArray(list) ? list.flatMap(b => b.images?.map(img => img.url) || []) : [];
          if (images.length > 0) {
            dispatch(setProductBannersAction(images));
          }
        }
      } catch (error) {
        console.error("Error fetching banners", error);
      }
    };
    fetchBanners();
  }, []);


  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const categoryParam = activeTab === 'ALL' ? 'ALL' : activeTab;
        const res = await httpClient.get(`${PRODUCT_URL}?keyword=&category=${categoryParam}&pageNo=${currentPage}&pageSize=${pageSize}`);
        if (res.data) {
          const list = res.data?.data || res.data?.list || res.data?.records || res.data || [];
          const total = res.data?.total || list.length;
          setProductListAction({ list: Array.isArray(list) ? list : [], total });
        }
      } catch (error) {
        console.error("Error fetching products", error);
      } finally {
        setLoading(false);
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [activeTab, currentPage, pageSize]);

  const featuredBGs = ['bg-[#dce6f2]', 'bg-[#e6ebf2]', 'bg-[#121212]', 'bg-[#f6eff2]'];
  const featuredProducts = featuredRawProducts.slice(0, 4).map((p, index) => ({
    ...p,
    id: p.productId || p._id || p.id,
    title: p.name || p.title,
    description: p.description || p.intro || p.subTitle || '',
    imageUrl: (p.images && p.images.length > 0) ? p.images[0] : (p.imageUrl || p.picUrl || p.coverImage || ''),
    bgColor: featuredBGs[index % featuredBGs.length]
  }));

  const paginatedProducts = products; // since we use API pagination now

  if (loading && bannerImages.length === 0) {
    return (
      <div className="w-full min-h-screen pb-16 bg-[#fcfdfe]">
        {/* Loading Banner Skeleton */}
        <div 
          className="w-full aspect-[21/9] sm:aspect-[24/9] md:h-[600px] bg-cover bg-center relative flex items-center"
        >
          <Skeleton.Image className="!w-full !h-full" active />
        </div>

        <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16 flex flex-col gap-16">
          {/* Section 1 Featured Skeletons */}
          <section className="w-full text-left">
            <div className="border-b border-gray-100 pb-4 mb-8">
              <Skeleton.Input active size="default" style={{ width: '120px' }} />
            </div>

            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="md:col-span-3 bg-gray-50 border border-gray-100 rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row justify-between items-center sm:items-stretch gap-6">
                  <div className="flex flex-col justify-between items-start flex-1 py-1 gap-4">
                    <div>
                      <Skeleton.Input active size="small" style={{ width: '80px' }} />
                      <div className="mt-3">
                        <Skeleton.Input active size="default" style={{ width: '180px' }} />
                      </div>
                      <div className="mt-3">
                        <Skeleton active paragraph={{ rows: 2 }} title={false} />
                      </div>
                    </div>
                    <Skeleton.Input active size="small" style={{ width: '100px' }} />
                  </div>
                  <div className="w-[180px] sm:w-[220px] aspect-square rounded-lg bg-white overflow-hidden shrink-0 flex items-center justify-center">
                    <Skeleton.Image className="!w-full !h-full" active />
                  </div>
                </div>

                <div className="md:col-span-2 bg-gray-50 border border-gray-100 rounded-xl p-6 sm:p-8 flex flex-col justify-between items-start gap-6">
                  <div className="w-full flex flex-col gap-3">
                    <Skeleton.Input active size="default" style={{ width: '140px' }} />
                    <Skeleton active paragraph={{ rows: 2 }} title={false} />
                  </div>
                  <div className="w-full flex justify-between items-end mt-auto gap-4">
                    <Skeleton.Input active size="small" style={{ width: '80px' }} />
                    <div className="w-[90px] h-[90px] rounded-lg bg-white overflow-hidden shrink-0 flex items-center justify-center">
                      <Skeleton.Image className="!w-full !h-full" active />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 Category listing skeletons */}
          <section className="w-full text-left">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end mb-6 border-b border-gray-100 pb-4 gap-4">
              <Skeleton.Input active size="default" style={{ width: '120px' }} />
              <div className="flex gap-4 sm:gap-6">
                {categories.map((tab) => (
                  <span key={tab._id || tab} className="text-gray-300 text-sm">{tab.name || tab}</span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 flex flex-col gap-4">
                  <div className="w-full aspect-[4/5] rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                    <Skeleton.Image className="!w-full !h-full" active />
                  </div>
                  <Skeleton.Input active size="small" style={{ width: '40%' }} />
                  <Skeleton.Input active size="default" style={{ width: '90%' }} />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pb-16 bg-[#fcfdfe]">

      {/* ── Product Hero Master Banner (Carousel) ── */}
      <div className="w-full aspect-[21/9] sm:aspect-[24/9] md:h-[600px] relative flex items-center overflow-hidden bg-[#1a1a1a] group">
        
        {/* Background Carousel */}
        <div className="absolute inset-0 z-0">
          {bannerImages.length > 0 ? (
            <Carousel ref={carouselRef} autoplay effect="fade" dots={bannerImages.length > 1 ? true : false} className="w-full h-full [&_.slick-dots]:bottom-6">
              {bannerImages.map((url, idx) => (
                <div key={idx} className="w-full aspect-[21/9] sm:aspect-[24/9] md:h-[600px]">
                  <div
                    className="w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${url})` }}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div
              className="w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${productHero})` }}
            />
          )}
          
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/45 z-0 pointer-events-none" />
        </div>

        {/* Navigation Arrows */}
        {bannerImages.length > 1 && (
          <>
            <button 
              onClick={() => carouselRef.current?.prev()}
              className="absolute left-4 sm:left-8 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer border-none"
            >
              <LeftOutlined />
            </button>
            <button 
              onClick={() => carouselRef.current?.next()}
              className="absolute right-4 sm:right-8 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer border-none"
            >
              <RightOutlined />
            </button>
          </>
        )}

        {/* Content banner (fixed on top) */}
        <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 w-full z-10 text-white relative pointer-events-none">
          <div className="max-w-[620px] flex flex-col gap-2.5 sm:gap-4 items-start text-left pointer-events-auto">
              {/* <span className="text-[10px] sm:text-xs font-bold tracking-widest text-indigo-400 bg-indigo-50/10 px-2.5 py-1 rounded">
                {t('text_product_banner_type')}
              </span> */}
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight m-0 drop-shadow-md">
                {t("text_product_banner_title")}
              </h1>
              <p className="text-gray-200 text-xs sm:text-[14px] leading-relaxed m-0 mt-2 drop-shadow">
                {t('text_product_banner_sub')}
              </p>
            </div>
          </div>
        </div>

      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16 flex flex-col gap-16 sm:gap-24">

        {/* ── Section 1: 甄选产品 (Featured Products) ── */}
        <section className="w-full text-left">
          <div className="border-b border-gray-100 pb-4 mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0 tracking-wide">
              {t('text_selected_product')}
            </h2>
            <p className="text-gray-400 text-xs mt-1.5 m-0">
              {t('text_selected_product_sub')}
            </p>
          </div>

          {/* Featured Grid Layout (Top 2 items wide, Bottom 2 items wide) */}
          <div className="flex flex-col gap-6">
            {/* First Row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 sm:gap-6">
              {/* Card 1 (Wide, spans 3 cols) */}
              {featuredProducts?.map((product, index) => (
                <div 
                  key={product.id} 
                  onClick={() => navigate(`/product/${product.id}`)}
                  className={`md:col-span-3 border border-[#cbd5e1]/40 rounded-xl flex flex-col sm:flex-row overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white cursor-pointer group`}
                >
                  
                  {/* Image - Top on mobile, Right on desktop */}
                  <div className="w-full sm:w-[220px] aspect-[4/3] sm:aspect-square overflow-hidden shrink-0 order-1 sm:order-2 relative">
                    <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {/* Optional badge */}
                    {index >= 2 && (
                      <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-md text-white text-[10px] px-2 py-0.5 rounded-full">
                        {t("text_new_product")}
                      </div>
                    )}
                  </div>

                  {/* Content - Bottom on mobile, Left on desktop */}
                  <div className="flex flex-col justify-between items-start flex-1 p-3 sm:p-8 order-2 sm:order-1">
                    <div>
                      <h3 className="text-[13px] sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2.5 line-clamp-1 group-hover:text-[#e2005a] transition-colors">
                        {product.title}
                      </h3>
                      <p className="text-gray-500 text-[11px] sm:text-[13px] leading-relaxed line-clamp-2 max-w-[280px]">
                         {stripHtml(product?.description)}
                      </p>
                    </div>
                    <span className="text-[12px] sm:text-[16px] bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent font-bold mt-3 sm:mt-6 flex items-center gap-1">
                      {t('text_buy_now')} &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Section 2: 产品分类 (Product Categories) ── */}
        <section className="w-full text-left">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end mb-6 border-b border-gray-100 pb-4 gap-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 m-0 tracking-wide">
              {t('text_product_type')}
            </h2>

            {/* Filter Tabs */}
            <div className="flex gap-4 sm:gap-6 text-[13px] sm:text-[14px] font-semibold overflow-x-auto [&::-webkit-scrollbar]:hidden">
              {categories.map((tab) => {
                const isActive = activeTab === (tab._id);
                return (
                  <div
                    key={tab._id}
                    onClick={() => { setActiveTab(tab._id || tab.id); setCurrentPage(1); }}
                    className={`cursor-pointer pb-2 relative transition-colors whitespace-nowrap duration-200 ${isActive ? 'text-black font-bold' : 'text-gray-400 hover:text-black'
                      }`}
                  >
                    {t(tab.name)}
                    {isActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#df97fb] via-[#7887fd] to-[#50c7f8] rounded-full" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Products listing 4x2 grid */}
          {loadingProducts ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 flex flex-col gap-4">
                  <div className="w-full aspect-[4/5] rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                    <Skeleton.Image className="!w-full !h-full" active />
                  </div>
                  <Skeleton.Input active size="small" style={{ width: '40%' }} />
                  <Skeleton.Input active size="default" style={{ width: '90%' }} />
                </div>
              ))}
            </div>
          ) : paginatedProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
              {paginatedProducts.map((p) => {
                  const pId = p.productId || p.id || p._id;
                  const pTitle = p.name || p.title;
                  const pImage = (p.images && p.images.length > 0) ? p.images[0] : (p.imageUrl || p.picUrl || '');
                  return (
                    <div
                      key={pId}
                      onClick={() => navigate(`/product/${pId}`)}
                      className="bg-white rounded-lg border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full cursor-pointer group"
                    >
                      <div className="w-full aspect-[4/5] overflow-hidden bg-gray-50 relative">
                        <img src={pImage} alt={pTitle} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <span className="text-[10px] text-gray-400 font-semibold tracking-wider uppercase mb-1">
                          {t(p.category || '')}
                        </span>
                        <h4 className="text-[13px] sm:text-[14px] font-bold text-gray-900 group-hover:text-[#e2005a] transition-colors line-clamp-2 leading-snug m-0 flex-1">
                          {pTitle}
                        </h4>
                        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                          <span className="text-[14px] font-bold text-[#e2005a]">¥{(p.price || 0).toFixed(2)}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/product/${pId}`);
                            }}
                            className="bg-gradient-to-r from-[#1e295d] to-[#0d163d] text-white text-[12px] font-semibold py-1.5 px-4 rounded border border-[#6b8cff]/50 hover:border-[#6b8cff] transition-all cursor-pointer shadow-sm whitespace-nowrap"
                          >
                            {t('text_purchase')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
          ) : (
            <Empty description={t('text_no_product')} />
          )}

          {/* Pagination */}
          {totalProducts > 0 && (
            <div className="flex justify-end mt-12">
              <CustomPagination
                current={currentPage}
                pageSize={pageSize}
                total={totalProducts}
                onChange={(page, limit) => {
                  if (limit !== pageSize) {
                    setPageSize(limit);
                    setCurrentPage(1);
                  } else {
                    setCurrentPage(page);
                  }
                }}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
